<template>
  <article class="external-resume-card">

    <div class="external-resume-card__last-visited">
      <BasicDate
        :date="resume.lastVisited.date"
        :title="resume.lastVisited.title"
      />
    </div>

    <aside class="external-resume-card__aside">
      <BasicAvatar
        :href="resume.href"
        :image="resume.avatar"
        size="medium"
        roundness="full"
      />
    </aside>

    <div class="external-resume-card__main">

      <div class="external-resume-card__body">

        <h2 class="external-resume-card__title">
          <a
            :href="resume.href"
            target="_blank"
            rel="noreferrer"
            class="external-resume-card__title-link"
            v-text="resume.title"
          />
        </h2>

        <div
          v-if="meta.length !== 0"
          class="external-resume-card__meta"
        >

          <InlineList
            :items="meta"
            v-slot="{ item }"
          >
            <span v-if="item.list">
              Работает в
              <InlineList
                :items="item.list"
                separator="comma"
                v-slot="{ item: listItem }"
              >
                <BasicLink
                  :href="listItem.href"
                  :title="listItem.title"
                  appearance="dark"
                />
              </InlineList>
            </span>
            <LocationItem
              v-else-if="item.location"
              :location="item.location"
            />
            <BasicLink
              v-else-if="item.href"
              :href="item.href"
              :title="item.title"
              appearance="dark"
            />
            <span
              v-else
              v-text="item.title"
            />
          </InlineList>
        </div>

        <div
          v-if="resume.careerHref"
          class="external-resume-card__career-link"
        >
          <BasicButton
            :href="resume.careerHref"
            appearance="badge"
          >
            <InlineAlign gap="small">
              <template #start>
                <SvgIcon source="/images/services.svg" icon="service-habr-career" size="16" />
              </template>
              <template #default>
                <span>Профиль на Хабр Карьере</span>
              </template>
            </InlineAlign>
          </BasicButton>
        </div>

        <div class="external-resume-card__sections">

          <SectionGroup gap="medium-large">
            <template v-if="type === 'habr'">
              <ExternalResumeSection v-if="resume.habr" type="habr" :resume="resume.habr" />
              <ExternalResumeSection v-if="resume.qa" type="qa" :resume="resume.qa" />
            </template>
            <template v-else>
              <ExternalResumeSection v-if="resume.qa" type="qa" :resume="resume.qa" />
              <ExternalResumeSection v-if="resume.habr" type="habr" :resume="resume.habr" />
            </template>
          </SectionGroup>

          <TransitionExpand v-if="resume.privateInfo">
            <div
              v-if="!collapsed"
              class="external-resume-card__collapsed-sections"
            >
              <ContentSection
                v-if="resume.privateInfo.lastActivity"
                appearance="card-section"
                title="Последняя активность"
              >
                {{ resume.privateInfo.lastActivity }}
              </ContentSection>

              <template v-if="resume.privateInfo.contribution">
                <ContentSection
                  appearance="card-section"
                  title="Общий вклад"
                >
                  {{ resume.privateInfo.contribution.total }}
                </ContentSection>

                <ContentSection
                  v-if="resume.privateInfo.contribution.tags"
                  appearance="card-section"
                  :title="resume.privateInfo.contribution.tags.title"
                >
                  {{ resume.privateInfo.contribution.tags.value }}
                </ContentSection>
              </template>

              <ContentSection
                v-if="resume.privateInfo.habrContacts"
                appearance="communities-card-section"
                title="Контакты с Хабра"
              >
                <ServiceIconList :services="resume.privateInfo.habrContacts" />
              </ContentSection>

              <ContentSection
                v-if="resume.privateInfo.qaContacts"
                appearance="communities-card-section"
                title="Контакты с Q&A"
              >
                <ServiceIconList :services="resume.privateInfo.qaContacts" />
              </ContentSection>
            </div>
          </TransitionExpand>
        </div>
      </div>
      <div class="external-resume-card__footer" v-if="resume.privateInfo">
        <div class="external-resume-card__collapse-button">
          <BasicButton
            appearance="tertiary"
            size="m"
            @click="collapsed = !collapsed"
          >
            {{buttonTitle}}
          </BasicButton>
        </div>
      </div>
    </div>
  </article>
</template>

<script>
  import BasicAvatar from "src/components/BasicAvatar/BasicAvatar.vue";
  import BasicButton from "src/components/BasicButton/BasicButton.vue";
  import BasicDate from "src/components/BasicDate/BasicDate.vue";
  import BasicLink from "src/components/BasicLink/BasicLink.vue";
  import SvgIcon from "src/components/icons/SvgIcon/SvgIcon.vue";
  import InlineAlign from "src/components/InlineAlign/InlineAlign.vue";
  import InlineCoworkersList from "src/components/InlineCoworkersList/InlineCoworkersList.vue";
  import InlineList from "src/components/InlineList/InlineList.vue";
  import LocationItem from 'src/components/LocationItem/LocationItem.vue';
  import ExternalResumeSection from 'src/components/resumes/ExternalResumeSection/ExternalResumeSection.vue';
  import ContentSection from "src/components/sections/ContentSection/ContentSection.vue";
  import SectionGroup from 'src/components/sections/SectionGroup/SectionGroup.vue';
  import ServiceIconList from 'src/components/ServiceIconList/ServiceIconList.vue';
  import TransitionExpand from "src/components/transitions/TransitionExpand/TransitionExpand.vue";

  export default {
    name: 'ExternalResumeCard',
    components: {
      ServiceIconList,
      LocationItem,
      ExternalResumeSection,
      SectionGroup,
      TransitionExpand,
      InlineCoworkersList,
      BasicButton,
      ContentSection,
      SvgIcon,
      InlineAlign,
      BasicLink,
      InlineList,
      BasicAvatar,
      BasicDate,
    },
    props: {
      resume: {
        type: Object,
        required: true,
      },
      type: {
        type: String,
        required: true,
      },
    },
    data() {
      return {
        collapsed: true,
      }
    },
    computed: {
      meta() {
        const { resume } = this;
        let result = [];

        if (resume.specialization) {
          result.push({ title: resume.specialization });
        }

        if (resume.companies) {
          result.push({
            list: resume.companies,
          });
        }

        if (resume.location) {
          result.push({ location: resume.location });
        }

        return result;
      },
      buttonTitle() {
        return this.collapsed ? 'Показать больше' : 'Свернуть';
      },
    }
  }
</script>